/** Календарная дата `YYYY-MM-DD`: такие строки сравниваются как даты. */
export type IsoDate = string;

/** Тип дня по производственному календарю студии: рабочий, сокращённый (на час короче) или выходной. */
export type DayType = 'work' | 'short' | 'off';

/**
 * Производственный календарь: ответ о любом дне. Календарь живёт в модуле `org`, в domain он приходит
 * готовой функцией (сценарий собирает её из исключений календаря на нужный период).
 */
export type WorkCalendar = (date: IsoDate) => DayType;

/** Предохранитель перебора дней: подряд больше 60 выходных в календаре не бывает. */
const GUARD_DAYS = 60;

export const isWorking = (calendar: WorkCalendar, date: IsoDate): boolean => calendar(date) !== 'off';

/** Дата через `days` календарных дней (можно отрицательное). */
export function addDays(date: IsoDate, days: number): IsoDate {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}

/** Сам день, если он рабочий, иначе ближайший следующий рабочий. */
export function nextWorkingDay(date: IsoDate, calendar: WorkCalendar): IsoDate {
  let day = date;
  for (let guard = 0; !isWorking(calendar, day) && guard < GUARD_DAYS; guard += 1) day = addDays(day, 1);
  return day;
}

/**
 * Сдвиг на `delta` рабочих дней: 0 — ближайший рабочий день не раньше `date` (`nextWorkingDay`);
 * положительный — вперёд, отрицательный — назад; результат всегда рабочий день. Сдвиг считается от
 * `nextWorkingDay(date)`: из субботы на +1 — во вторник (понедельник — шаг 0).
 */
export function shiftWorkingDays(date: IsoDate, delta: number, calendar: WorkCalendar): IsoDate {
  let day = nextWorkingDay(date, calendar);
  const step = delta < 0 ? -1 : 1;
  let left = Math.abs(Math.trunc(delta));
  for (let guard = 0; left > 0 && guard < Math.abs(delta) * 7 + GUARD_DAYS; guard += 1) {
    day = addDays(day, step);
    if (isWorking(calendar, day)) left -= 1;
  }
  return day;
}

/** Рабочие дни отрезка `[from, to]` включительно, по порядку; `from > to` — пусто. */
export function workingDates(from: IsoDate, to: IsoDate, calendar: WorkCalendar): IsoDate[] {
  const dates: IsoDate[] = [];
  for (let day = from; day <= to; day = addDays(day, 1)) {
    if (isWorking(calendar, day)) dates.push(day);
  }
  return dates;
}

/** Сколько рабочих дней в отрезке `[from, to]` включительно. */
export function workingDaysIn(from: IsoDate, to: IsoDate, calendar: WorkCalendar): number {
  return workingDates(from, to, calendar).length;
}
